<script lang="ts">
  // The board of a project (maquette Board.dc.html): four columns, À faire, En cours,
  // En attente (the turn is with someone else than the assignee) and Terminé (30 days).
  // Drag and drop changes the status and the order; dropping into « En attente » asks to
  // whom the turn goes. Below 760 px: one column at a time, chosen at the top, and the
  // status changes from the card.
  import { api, ApiError, type Conversation, type DisplayStatus, type Message, type TaskStatus } from '../lib/api';
  import { router, threadPath } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import { doneText, dueText, isOverdue, sinceText, STATUS_LABEL } from '../lib/tasks';
  import Avatar from './Avatar.svelte';
  import TaskDialog from './TaskDialog.svelte';

  let { conversation }: { conversation: Conversation } = $props();

  let error = $state('');
  let person = $state<string | null>(null);
  let creating = $state(false);
  let handoffFor = $state<Message | null>(null);
  let mobileColumn = $state<DisplayStatus>('todo');
  let showAllDone = $state(false);
  const DONE_SHOWN = 5;

  $effect(() => {
    const id = conversation.id;
    app.loadBoard(id).catch((e) => (error = e instanceof ApiError ? e.message : 'Impossible de charger les tâches'));
  });

  const all = $derived((app.boards[conversation.id] ?? []).filter((m) => m.task && !m.deleted));
  const tasks = $derived(person ? all.filter((m) => m.task!.assignee?.id === person) : all);
  const COLUMNS: { key: DisplayStatus; dot: string }[] = [
    { key: 'todo', dot: 'var(--status-todo)' },
    { key: 'doing', dot: 'var(--status-doing)' },
    { key: 'waiting', dot: 'var(--status-waiting)' },
    { key: 'done', dot: 'var(--status-done)' },
  ];
  const byColumn = $derived(
    Object.fromEntries(
      COLUMNS.map((c) => {
        let list = tasks.filter((m) => m.task!.display_status === c.key);
        if (c.key === 'done') list = [...list].sort((a, b) => (b.task!.done_at ?? '').localeCompare(a.task!.done_at ?? ''));
        return [c.key, list];
      }),
    ) as Record<DisplayStatus, Message[]>,
  );
  const counts = $derived({
    done: all.filter((m) => m.task!.status === 'done').length,
    doing: all.filter((m) => m.task!.display_status === 'doing').length,
    waiting: all.filter((m) => m.task!.display_status === 'waiting').length,
    todo: all.filter((m) => m.task!.display_status === 'todo').length,
  });
  /** Open tasks per assignee, for the filter. */
  const people = $derived.by(() => {
    const map = new Map<string, { user: NonNullable<NonNullable<Message['task']>['assignee']>; n: number }>();
    for (const m of all) {
      const a = m.task!.assignee;
      if (!a || m.task!.status === 'done') continue;
      map.set(a.id, { user: a, n: (map.get(a.id)?.n ?? 0) + 1 });
    }
    return [...map.values()].sort((x, y) => y.n - x.n);
  });

  async function update(task: Message, change: Parameters<typeof api.updateTask>[1]) {
    error = '';
    try {
      app.upsertTask(await api.updateTask(task.id, change));
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
  }

  // --- Drag and drop -------------------------------------------------------------------
  let dragged = $state<string | null>(null);
  let over = $state<DisplayStatus | null>(null);

  /** Between the card dropped on and the one before it, or at the end of the column. */
  function positionBefore(column: Message[], beforeId: string | null): number {
    const list = column.filter((m) => m.id !== dragged);
    if (!beforeId) return (list.at(-1)?.task!.position ?? 0) + 1;
    const index = list.findIndex((m) => m.id === beforeId);
    const next = list[index]!.task!.position;
    const previous = index > 0 ? list[index - 1]!.task!.position : next - 2;
    return (previous + next) / 2;
  }

  function drop(column: DisplayStatus, beforeId: string | null) {
    const task = all.find((m) => m.id === dragged);
    over = null;
    dragged = null;
    if (!task) return;
    const from = task.task!.display_status;
    if (column === 'waiting') {
      if (from !== 'waiting') handoffFor = task;
      return;
    }
    if (from === 'waiting' && column !== 'done') {
      error = "Cette tâche attend quelqu'un : ouvrez-la pour reprendre la main.";
      return;
    }
    const position = positionBefore(byColumn[column], beforeId === task.id ? null : beforeId);
    const status = column as TaskStatus;
    void update(task, from === column ? { position } : { status, position });
  }

  const open = (m: Message) => router.go(threadPath(m.conversation_id, m.id));
</script>

<div class="board">
  <div class="toolbar">
    <div class="progress">
      <span><strong>{counts.done} sur {all.length}</strong> <span class="muted">terminées</span></span>
      <div class="bar" role="img" aria-label="{all.length ? Math.round((counts.done / all.length) * 100) : 0} % terminé">
        <span style:flex={counts.done} style:background="var(--status-done)"></span>
        <span style:flex={counts.doing} style:background="var(--status-doing)"></span>
        <span style:flex={counts.waiting} style:background="var(--status-waiting)"></span>
        <span style:flex={counts.todo} style:background="var(--status-todo-soft)"></span>
      </div>
    </div>
    <div class="people" role="group" aria-label="Filtrer par personne">
      <button type="button" class="chip" aria-pressed={person === null} onclick={() => (person = null)}>Tout le monde</button>
      {#each people as p (p.user.id)}
        <button
          type="button"
          class="chip person"
          aria-pressed={person === p.user.id}
          aria-label="{p.user.display_name}, {p.n} tâche{p.n > 1 ? 's' : ''}"
          onclick={() => (person = person === p.user.id ? null : p.user.id)}
        >
          <Avatar user={p.user} size={24} />{p.n}
        </button>
      {/each}
    </div>
    <span class="spacer"></span>
    {#if conversation.is_project && !conversation.archived_at}
      <button type="button" class="btn primary new" onclick={() => (creating = true)}>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
        Nouvelle tâche
      </button>
    {/if}
  </div>
  {#if error}<p class="error" role="alert">{error}</p>{/if}

  <div class="mobile-switch" role="tablist" aria-label="Colonne affichée">
    {#each COLUMNS as c (c.key)}
      <button type="button" role="tab" aria-selected={mobileColumn === c.key} onclick={() => (mobileColumn = c.key)}>
        {STATUS_LABEL[c.key]} <span class="n">{byColumn[c.key].length}</span>
      </button>
    {/each}
  </div>

  <div class="columns">
    {#each COLUMNS as c (c.key)}
      {@const list = c.key === 'done' && !showAllDone ? byColumn.done.slice(0, DONE_SHOWN) : byColumn[c.key]}
      <section
        class="column"
        class:over={over === c.key}
        class:mobile-hidden={mobileColumn !== c.key}
        aria-labelledby="col-{c.key}"
        ondragover={(e) => {
          if (!dragged) return;
          e.preventDefault();
          over = c.key;
        }}
        ondragleave={(e) => {
          if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) over = null;
        }}
        ondrop={(e) => {
          e.preventDefault();
          drop(c.key, null);
        }}
      >
        <h2 id="col-{c.key}"><span class="dot" style:background={c.dot} aria-hidden="true"></span><span class="name">{STATUS_LABEL[c.key]}</span><span class="n">{byColumn[c.key].length}</span></h2>
        {#each list as m (m.id)}
          {@const t = m.task!}
          <div
            class="card"
            class:dragging={dragged === m.id}
            draggable={conversation.is_project && !conversation.archived_at}
            role="listitem"
            ondragstart={(e) => {
              dragged = m.id;
              e.dataTransfer?.setData('text/plain', m.id);
            }}
            ondragend={() => {
              dragged = null;
              over = null;
            }}
            ondragover={(e) => {
              if (dragged) e.preventDefault();
            }}
            ondrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              drop(c.key, m.id);
            }}
          >
            <a href={threadPath(m.conversation_id, m.id)} class="title" class:done={t.status === 'done'} onclick={(e) => { e.preventDefault(); open(m); }}>{t.title}</a>
            <span class="meta">
              {#if t.assignee}<Avatar user={t.assignee} size={22} />{:else}<span class="nobody" title="Non attribuée">?</span>{/if}
              {#if c.key === 'waiting' && t.waiting_on}
                <span class="due waiting">Attend {t.waiting_on.display_name.split(' ')[0]} · {sinceText(t.waiting_since)}</span>
              {:else if c.key === 'done' && t.done_at}
                <span class="due">terminée {doneText(t.done_at)}</span>
              {:else}
                <span class="due" class:late={isOverdue(t)}>{dueText(t)}</span>
              {/if}
              {#if m.reply_count > 0}<span class="comments">{m.reply_count} comm.</span>{/if}
            </span>
            <label class="sr-only" for="status-{m.id}">Statut de {t.title}</label>
            <select
              id="status-{m.id}"
              class="mobile-status"
              value={t.status}
              disabled={!conversation.is_project || !!conversation.archived_at}
              onchange={(e) => update(m, { status: e.currentTarget.value as TaskStatus })}
            >
              <option value="todo">À faire</option>
              <option value="doing">En cours</option>
              <option value="done">Terminé</option>
            </select>
          </div>
        {:else}
          <p class="empty">{c.key === 'waiting' ? 'Déposez une tâche ici pour passer la main.' : 'Aucune tâche'}</p>
        {/each}
        {#if c.key === 'done' && byColumn.done.length > DONE_SHOWN}
          <button type="button" class="more" onclick={() => (showAllDone = !showAllDone)}>
            {showAllDone ? 'Réduire' : `Voir les ${byColumn.done.length - DONE_SHOWN} autres`}
          </button>
        {/if}
      </section>
    {/each}
  </div>
</div>

{#if creating}<TaskDialog {conversation} onClose={() => (creating = false)} />{/if}
{#if handoffFor}<TaskDialog {conversation} {handoffFor} onClose={() => (handoffFor = null)} />{/if}

<style>
  .board {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
  }
  .toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px 24px;
  }
  .progress {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 220px;
    font-size: 13px;
  }
  .muted {
    color: var(--muted);
  }
  .bar {
    display: flex;
    width: 220px;
    height: 8px;
    border-radius: 4px;
    overflow: hidden;
    background: var(--status-todo-soft);
    gap: 2px;
  }
  .people {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .chip {
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--border-strong);
    border-radius: 16px;
    background: var(--bg);
    color: var(--ink-2);
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .chip.person {
    padding: 0 10px 0 4px;
  }
  .chip[aria-pressed='true'] {
    background: var(--ink);
    border-color: var(--ink);
    color: var(--bg);
    font-weight: 600;
  }
  .spacer {
    flex: 1;
  }
  .new {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .error {
    margin: 0 24px 8px;
  }
  .columns {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(4, minmax(220px, 1fr));
    gap: 16px;
    padding: 0 24px 24px;
    align-items: start;
  }
  .column {
    background: var(--bg-page);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 420px;
    border: 2px dashed transparent;
  }
  .column.over {
    border-color: var(--accent-border);
    background: var(--accent-soft);
  }
  h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 4px;
    font-size: 14px;
    font-weight: 600;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 5px;
  }
  .name {
    flex: 1;
  }
  .n {
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }
  .card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    cursor: grab;
  }
  .card.dragging {
    opacity: 0.5;
  }
  .title {
    font-weight: 500;
    color: var(--ink);
    text-decoration: none;
    overflow-wrap: anywhere;
  }
  .title:hover {
    text-decoration: underline;
  }
  .title.done {
    color: var(--muted);
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: var(--muted);
  }
  .nobody {
    width: 22px;
    height: 22px;
    border-radius: 11px;
    border: 1px dashed var(--border-strong);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .due {
    flex: 1;
  }
  .due.late {
    color: var(--danger);
    font-weight: 600;
  }
  .due.waiting {
    color: var(--status-waiting-text);
    font-weight: 600;
  }
  .empty {
    margin: 4px;
    font-size: 13px;
    color: var(--muted);
  }
  .more {
    height: 32px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    padding: 0 4px;
  }
  .mobile-switch,
  .mobile-status {
    display: none;
  }
  @media (max-width: 1099px) {
    .columns {
      overflow-x: auto;
    }
  }
  @media (max-width: 759px) {
    .toolbar {
      padding: 12px;
    }
    .mobile-switch {
      display: flex;
      gap: 4px;
      padding: 0 12px 12px;
      overflow-x: auto;
    }
    .mobile-switch button {
      flex: 0 0 auto;
      height: 34px;
      padding: 0 12px;
      border: 1px solid var(--border);
      border-radius: 17px;
      background: var(--bg);
      font-size: 13px;
    }
    .mobile-switch button[aria-selected='true'] {
      background: var(--accent-soft);
      border-color: var(--accent-border);
      color: var(--accent-on-soft);
      font-weight: 600;
    }
    .columns {
      grid-template-columns: minmax(0, 1fr);
      padding: 0 12px 12px;
    }
    .column {
      min-height: 0;
    }
    .column.mobile-hidden {
      display: none;
    }
    .mobile-status {
      display: block;
      align-self: flex-start;
      height: 32px;
    }
    .card {
      cursor: default;
    }
  }
</style>
